import React, { useState } from "react";
import { MessagesSquare, Plus, Trash2, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

const starterTree = (name) => ({
  start: "d1",
  nodes: [
    { id: "d1", text: `Hello traveler! I'm ${name || "a friend"}. Can you help me?`, choices: [
      { label: "Of course — what do you need?", next: "d2" },
      { label: "What's in it for me?", next: "d3" },
    ] },
    { id: "d2", text: "Wonderful! Bring me some materials and I'll reward you.", choices: [] },
    { id: "d3", text: "A fair question — here, take this to start!", choices: [
      { label: "Thanks!", coins: 5, end: true },
    ] },
  ],
});

// Per-NPC branching dialogue tree editor. Writes config.npcs[index].dialogueTree.
export default function NpcDialogueEditor({ npc, index, config, setField }) {
  const [open, setOpen] = useState(false);
  const tree = npc?.dialogueTree && Array.isArray(npc.dialogueTree.nodes) ? npc.dialogueTree : null;
  const nodes = tree?.nodes || [];

  const itemOptions = [
    ...((config?.items) || []).map((it) => it.id).filter(Boolean),
    ...((config?.crafting) || []).map((r) => r?.output?.id).filter(Boolean),
  ].filter((v, i, a) => v && a.indexOf(v) === i);

  const write = (t) => setField(`npcs.${index}.dialogueTree`, t);
  const seed = () => { write(starterTree(npc?.name || npc?.label)); setOpen(true); };
  const clearTree = () => write(null);

  const setNodes = (arr) => write({ start: tree?.start && arr.some((n) => n.id === tree.start) ? tree.start : (arr[0]?.id || "d1"), nodes: arr });
  const updateNode = (ni, patch) => setNodes(nodes.map((n, i) => (i === ni ? { ...n, ...patch } : n)));
  const addNode = () => {
    let k = nodes.length + 1; const ids = new Set(nodes.map((n) => n.id));
    while (ids.has("d" + k)) k++;
    setNodes([...nodes, { id: "d" + k, text: "New line of dialogue.", choices: [] }]);
  };
  const removeNode = (ni) => setNodes(nodes.filter((_, i) => i !== ni));

  const setChoices = (ni, arr) => updateNode(ni, { choices: arr });
  const nodeIdOptions = nodes.map((n) => n.id);

  return (
    <div className="rounded-lg border border-[hsl(var(--border))]/70 bg-[hsl(var(--muted))]/20" data-testid={`npc-dialogue-${index}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid={`npc-dialogue-toggle-${index}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <MessagesSquare className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        Dialogue tree {tree ? `(${nodes.length} node${nodes.length === 1 ? "" : "s"})` : "— none"}
      </button>

      {open && (
        <div className="px-2.5 pb-2.5 space-y-2">
          {!tree ? (
            <button type="button" onClick={seed} data-testid={`npc-dialogue-add-${index}`}
              className="w-full text-[11px] font-semibold px-2 py-2 rounded-lg border border-dashed border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 flex items-center justify-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Add a branching conversation
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-[hsl(var(--muted-foreground))]">Starts at:</span>
                <Select value={tree.start} onValueChange={(v) => write({ ...tree, start: v })}>
                  <SelectTrigger className="h-7 w-24 text-xs" data-testid={`npc-dialogue-start-${index}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{nodeIdOptions.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                </Select>
                <button type="button" onClick={clearTree} className="ml-auto text-[10px] text-rose-500 hover:underline" data-testid={`npc-dialogue-clear-${index}`}>Remove tree</button>
              </div>

              {nodes.map((node, ni) => {
                const choices = Array.isArray(node.choices) ? node.choices : [];
                const ttEntries = node.titleText && typeof node.titleText === "object" ? Object.entries(node.titleText) : [];
                const ttTitle = ttEntries[0]?.[0] || "";
                const ttText = ttEntries[0]?.[1] || "";
                const setTitleLine = (title, txt) => { const key = (title || "").trim(); updateNode(ni, { titleText: (key && txt) ? { [key]: txt } : undefined }); };
                return (
                  <div key={ni} className="rounded-lg border border-[hsl(var(--border))] p-2 space-y-1.5" data-testid={`dlg-node-${index}-${ni}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">{node.id}</span>
                      <button type="button" onClick={() => removeNode(ni)} className="ml-auto h-6 w-6 grid place-items-center rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`dlg-node-remove-${index}-${ni}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <textarea value={node.text || ""} onChange={(e) => updateNode(ni, { text: e.target.value.slice(0, 280) })} rows={2}
                      placeholder="What the NPC says…  (tip: use {title} to greet by the player's proudest title)" data-testid={`dlg-node-text-${index}-${ni}`}
                      className="w-full text-xs rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 resize-none" />
                    <div className="flex flex-wrap items-center gap-1 pl-1" data-testid={`dlg-node-titleline-${index}-${ni}`}>
                      <span className="text-[10px] font-semibold text-[hsl(var(--muted-foreground))]">🏅 If player holds</span>
                      <Input value={ttTitle} onChange={(e) => setTitleLine(e.target.value, ttText)} placeholder="title name" className="h-7 w-28 text-xs" data-testid={`dlg-node-titlename-${index}-${ni}`} />
                      <Input value={ttText} onChange={(e) => setTitleLine(ttTitle, e.target.value.slice(0, 280))} placeholder="say this special line instead…" className="h-7 flex-1 min-w-[140px] text-xs" data-testid={`dlg-node-titletext-${index}-${ni}`} />
                    </div>
                    <div className="text-[10px] font-semibold text-[hsl(var(--muted-foreground))]">Player choices:</div>
                    {choices.map((c, ci) => (
                      <div key={ci} className="flex flex-wrap items-center gap-1 pl-1" data-testid={`dlg-choice-${index}-${ni}-${ci}`}>
                        <Input value={c.label || ""} onChange={(e) => setChoices(ni, choices.map((x, k) => (k === ci ? { ...x, label: e.target.value } : x)))} placeholder="Choice text" className="h-7 flex-1 min-w-[120px] text-xs" data-testid={`dlg-choice-label-${index}-${ni}-${ci}`} />
                        <Select value={c.end ? "__end__" : (c.next || NONE)} onValueChange={(v) => setChoices(ni, choices.map((x, k) => (k === ci ? { ...x, next: v === "__end__" || v === NONE ? undefined : v, end: v === "__end__" ? true : undefined } : x)))}>
                          <SelectTrigger className="h-7 w-[104px] text-xs" data-testid={`dlg-choice-next-${index}-${ni}-${ci}`}><SelectValue placeholder="Goes to…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__end__">↦ End chat</SelectItem>
                            {nodeIdOptions.filter((id) => id !== node.id).map((id) => <SelectItem key={id} value={id}>→ {id}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {itemOptions.length > 0 && (
                          <Select value={c.give || NONE} onValueChange={(v) => setChoices(ni, choices.map((x, k) => (k === ci ? { ...x, give: v === NONE ? undefined : v } : x)))}>
                            <SelectTrigger className="h-7 w-[92px] text-xs" data-testid={`dlg-choice-give-${index}-${ni}-${ci}`}><SelectValue placeholder="gift" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>no gift</SelectItem>
                              {itemOptions.map((id) => <SelectItem key={id} value={id}>🎁 {id}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        <Input type="number" value={c.coins || 0} onChange={(e) => setChoices(ni, choices.map((x, k) => (k === ci ? { ...x, coins: +e.target.value || 0 } : x)))} title="coins reward" className="h-7 w-14 text-center text-xs" data-testid={`dlg-choice-coins-${index}-${ni}-${ci}`} />
                        <label className="flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--muted-foreground))] cursor-pointer" title="This choice accepts the next quest" data-testid={`dlg-choice-quest-${index}-${ni}-${ci}`}>
                          <input type="checkbox" checked={!!c.quest} onChange={(e) => setChoices(ni, choices.map((x, k) => (k === ci ? { ...x, quest: e.target.checked ? true : undefined } : x)))} className="accent-[hsl(var(--primary))]" />
                          📜
                        </label>
                        <button type="button" onClick={() => setChoices(ni, choices.filter((_, k) => k !== ci))} className="h-7 w-7 grid place-items-center rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`dlg-choice-remove-${index}-${ni}-${ci}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setChoices(ni, [...choices, { label: "New choice", next: undefined, end: true }])} className="text-[10px] font-semibold text-[hsl(var(--primary))] flex items-center gap-1 pl-1" data-testid={`dlg-add-choice-${index}-${ni}`}><Plus className="w-3 h-3" /> Add choice</button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={addNode} className="w-full h-7 text-[11px]" data-testid={`dlg-add-node-${index}`}><Plus className="w-3 h-3" /> Add dialogue node</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
